import {
  DEFAULT_FILE_NAME_MAX_LENGTH,
  DEFAULT_INVALID_FILE_NAME_REGEX,
  extensionValidator,
  FileManager,
  fileSizeValidator,
  FileState,
  FileValidator,
  Heading,
  InlineNotification,
  nameLengthValidator,
  nameValidator,
  NewFile,
  NewOrUploadedFile,
  RecordContent,
  RecordFooter,
  UploadedFile,
  useTitleChange,
} from '@msaf/core-react'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormDelegate } from '../../../../../types/routing-types'
import ConfirmTransition from '../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../components/errors/messages'
import PublicDocumentLinks from '../../../../components/well-forms/documents/public-document-links'
import WellFileMetadata, { WellFileProps } from '../../../../components/well-forms/documents/well-file-meta-data'
import WellEditButtons, { ActionProps } from '../../../../components/wells/well-edit-buttons'
import { useWellState } from '../../../../context/well-context'
import { useGetWellData, useSaveFile, useSaveWellData } from '../../../../hooks/use-api'
import useEditForm from '../../../../hooks/use-edit-form'
import useToastMessage from '../../../../hooks/use-toast-message'
import { useWellId } from '../../../../hooks/use-well-id'
import { MaxFileSize, WELLS_DEFAULT_EXTENSION_TYPES, WELLS_DEFAULT_MIME_TYPES } from '../../../../services/constants'

interface EditWellAdditionalDocumentsProps extends FormDelegate {
  isAGSUpload?: boolean
  triggerSave?: boolean
  setTriggerSave?: Dispatch<SetStateAction<boolean>>
  setIsAGSModalOpen?: Dispatch<SetStateAction<boolean>>
}

export function EditWellAdditionalDocuments({
  setIsDirty,
  isAGSUpload,
  triggerSave,
  setTriggerSave,
  setIsAGSModalOpen,
}: EditWellAdditionalDocumentsProps) {
  const {
    wellState: { isDraft },
  } = useWellState()
  useTitleChange(`${isDraft ? 'New' : 'Edit'} Well | Additional Documents`)
  const id = useWellId()
  const navigate = useNavigate()
  const continueUrl = isDraft ? `/wells/${id}/edit/submission` : `/wells/${id}/view/external-document-references`
  const filesUrl = `wells/${id}/additional-documents/upload`
  const url = isAGSUpload ? `ags-upload/${id}/upload` : `wells/${id}/additional-documents/edit`

  const saveFilesQuery = useSaveFile(filesUrl)
  const stateQueryResult = useGetWellData(url)
  const saveMutationResult = useSaveWellData(url)
  const { data: publicLinksData } = useGetWellData(`wells/${id}/additional-documents/view`)
  const [isLoadingButtons, setIsLoadingButtons] = useState(true)

  const {
    fields,
    isSaveError,
    error,
    isLoading,
    isSaved,
    isSuccess,
    handleSave,
    addField,
    editFieldById,
    removeFieldById,
    validationErrors,
  } = useEditForm<{ files: WellFileProps[] }>({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  // MIME type for AGS/AQT is '' in file.type on client. This occurs when the file type cannot be determined.
  // Google / Python suggests 'application/octet-stream', while system file --mime-type *.ags suggests 'text/plain'
  const customMimeTypeValidator = (fileTypes: String[] = WELLS_DEFAULT_MIME_TYPES): FileValidator => {
    return (file: File) => {
      const fileExtension = file.name.split('.')[1].toLowerCase()
      const allowedUnknownMimeTypeExtensions = ['ags', 'aqt']
      if (
        !fileTypes.includes(file.type) &&
        !(file.type === '' && allowedUnknownMimeTypeExtensions.includes(fileExtension))
      ) {
        return {
          isValid: false,
          error: 'Invalid file type.',
        }
      }

      return { isValid: true }
    }
  }

  const fileValidators: FileValidator[] = [
    nameValidator(DEFAULT_INVALID_FILE_NAME_REGEX),
    // AGS Upload only allow .ags, .AGS otherwise allow all default including AGS
    extensionValidator(isAGSUpload ? ['ags', 'AGS'] : WELLS_DEFAULT_EXTENSION_TYPES),
    nameLengthValidator(DEFAULT_FILE_NAME_MAX_LENGTH),
    fileSizeValidator(MaxFileSize.MAX_SIZE),
    customMimeTypeValidator(),
  ]

  const actions: Array<ActionProps> = [
    {
      label: 'Save and continue',
      buttonStyle: 'primary',
      onClick: () =>
        handleSave(() => navigate(continueUrl, { state: { isProgrammaticTransition: true, prevFormSaved: true } })),
    },
    {
      label: 'Save',
      isDisabled: (fields?.files?.length ?? 0) > 10,
      buttonStyle: 'primary',
      onClick: () => handleSave(),
    },
    {
      label: 'Return to view',
      buttonStyle: 'text-action',
      onClick: () => navigate(`/wells/${id}/view/additional-documents`),
    },
  ]

  useEffect(() => {
    // Once AGS upload is saved, push back to details page
    if (setIsAGSModalOpen && isSaved) {
      setIsAGSModalOpen(false)
      navigate(`/wells/${id}/edit/details`, { state: { isRefetchFromAGSUpload: true, isProgrammaticTransition: true } })
    }
    // Set save flag to false for another attempt
    if (setTriggerSave) setTriggerSave(false)
  }, [isAGSUpload, isSaved, setIsAGSModalOpen, id, setTriggerSave, navigate])

  useToastMessage({
    errorMessage: isAGSUpload ? ERROR_MESSAGES.UPLOAD_FAILED : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: isAGSUpload ? 'AGS upload successful' : 'Save successful',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  // Trigger form save for AGS Modal
  useEffect(() => {
    if (triggerSave) {
      handleSave()
    }
    // Disable due to suggestions of handleSave
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSave])

  useEffect(() => {
    if (fields && isSuccess) {
      setIsLoadingButtons(false)
    }
  }, [fields, isSuccess])

  useEffect(() => {
    fields.files?.map((f) => f.state !== FileState.Saved)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaved])

  const defaultWellFileFields = {
    title: '',
    other: '',
  }

  const updateFile = (fileId: string, props: Partial<WellFileProps>) => {
    editFieldById('files', props, fileId)
  }

  function onFileUploading(file: NewFile) {
    setIsDirty(true)
    addField('files', {
      ...defaultWellFileFields,
      ...file,
    })
  }

  function onFileUploaded(file: UploadedFile, id: string) {
    editFieldById('files', file, id)
  }

  function onFileRemove(file: NewOrUploadedFile) {
    setIsDirty(true)
    removeFieldById('files', file.id)
  }

  function isUploadAllowed() {
    if (isAGSUpload) {
      return (fields?.files?.length ?? 0) < 1
    }
    return (fields?.files?.length ?? 0) < 10
  }

  return (
    <>
      <RecordContent>
        {(fields?.files?.length ?? 0) >= 10 && (
          <InlineNotification messageType='warning' isDismissable={false}>
            A maximum of ten documents can be uploaded for a well record.
          </InlineNotification>
        )}
        <Heading isSkeleton={isLoading} level={2}>
          {isAGSUpload ? 'Upload an AGS file' : 'Documents'}
        </Heading>
        <FileManager
          files={fields.files ?? []}
          isUploadAllowed={isUploadAllowed()}
          isEditable
          allowMultiple={false}
          allowedFileTypes={isAGSUpload ? ['.ags'] : undefined}
          validationErrors={[{ files: validationErrors['files'] ?? [] }]}
          isSkeleton={isLoading}
          getMetadataElement={(...args) => WellFileMetadata(...args, isAGSUpload ?? false, updateFile)}
          fileSaveAction={saveFilesQuery}
          onFileUploading={onFileUploading}
          onFileUploaded={onFileUploaded}
          onFileRemove={onFileRemove}
          fileValidators={fileValidators}
        />
        {!isAGSUpload && (
          <PublicDocumentLinks
            links={publicLinksData?.state.publicDocumentLinks ?? []}
            editNotice='Only linked public documents are shown below. To edit them, go to the External Document References area.'
          />
        )}
      </RecordContent>
      {!isAGSUpload && (
        <RecordFooter areActionsFixed={true}>
          <WellEditButtons isLoading={isLoadingButtons} actions={actions} />
        </RecordFooter>
      )}
    </>
  )
}

export default function EditWellAdditionalDocumentsWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <EditWellAdditionalDocuments setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
